import React, { useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import axios from 'axios';

import { db, analytic } from '../../server/firebase';
import AuthContex from '../../context/autenticacion/authContex';
import FooterBeparking from '../../components/footer/beparking';
import { URL_AWS } from '../../constans/servidor';

/**
 * Firestore solo permite ciertas colecciones en las reglas actuales.
 * PuntosServicioBeParking está denegada; usamos Promociones con cmsTipo.
 */
const COLLECTION = 'Promociones';
const CMS_TIPO = 'puntoServicio';
const MAX_LEN = 70;
const emptyForm = { nombre: '', direccion: '', ciudad: '' };

const PuntosbePage = () => {
  const authContex = useContext(AuthContex);
  const { usuario } = authContex;
  const isAdmin =
    Boolean(usuario?.cms) || localStorage.getItem('token') === 'FIREBASE';

  const [loading, setLoading] = useState(true);
  const [historial, setHistorial] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [adding, setAdding] = useState(false);

  const [showDelete, setShowDelete] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const mapDoc = (doc) => ({
    id: doc.id,
    nombre: doc.data().nombre || '',
    direccion: doc.data().direccion || '',
    ciudad: doc.data().ciudad || '',
    orden: doc.data().orden ?? 0,
  });

  const mapApiItem = (item, index) => ({
    id: `api-${index}-${item.nombre_comercial || item.nombre || ''}`,
    nombre: String(item.nombre_comercial || item.nombre || ''),
    direccion: String(item.direccion || ''),
    ciudad: String(item.Ciudad || item.ciudad || ''),
    orden: index + 1,
    fromApi: true,
  });

  const cargarDesdeApi = async () => {
    const result = await axios.post(
      `${URL_AWS}/serviciosBeparking/rest/beparking/getServicePoints`
    );
    const data = result?.data?.data || [];
    if (!Array.isArray(data)) return [];
    return data.map(mapApiItem);
  };

  const cargarDesdeFirestore = async () => {
    try {
      const snap = await db.collection(COLLECTION).get();
      const items = snap.docs
        .filter((doc) => doc.data().cmsTipo === CMS_TIPO)
        .map(mapDoc);
      items.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      return items;
    } catch (e) {
      console.warn('Firestore puntos de servicio no disponible:', e);
      return null;
    }
  };

  const intentarSembrarFirestore = async (apiItems) => {
    if (!isAdmin || !apiItems.length) return null;
    try {
      // Firestore batch máx. 500 operaciones
      const chunkSize = 400;
      for (let i = 0; i < apiItems.length; i += chunkSize) {
        const chunk = apiItems.slice(i, i + chunkSize);
        const batch = db.batch();
        chunk.forEach((item, idx) => {
          const ref = db.collection(COLLECTION).doc();
          batch.set(ref, {
            cmsTipo: CMS_TIPO,
            nombre: String(item.nombre || '').slice(0, MAX_LEN),
            direccion: String(item.direccion || '').slice(0, MAX_LEN),
            ciudad: String(item.ciudad || '').slice(0, MAX_LEN),
            orden: i + idx + 1,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
        });
        await batch.commit();
      }
      return cargarDesdeFirestore();
    } catch (e) {
      console.warn('No se pudo sembrar Firestore:', e);
      toast.error(
        'No se pudo guardar en Firestore. Verifica que iniciaste sesión CMS (/login).'
      );
      return null;
    }
  };

  const cargarHistorial = useCallback(async () => {
    setLoading(true);
    try {
      const firestoreItems = await cargarDesdeFirestore();
      if (firestoreItems && firestoreItems.length > 0) {
        setHistorial(firestoreItems);
        return;
      }

      const apiItems = await cargarDesdeApi();
      if (firestoreItems && firestoreItems.length === 0 && isAdmin) {
        const sembrados = await intentarSembrarFirestore(apiItems);
        if (sembrados && sembrados.length > 0) {
          toast.success('Puntos sincronizados en Firestore');
          setHistorial(sembrados);
          return;
        }
      }
      setHistorial(apiItems);
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron cargar los puntos de servicio');
      setHistorial([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    analytic.logEvent('page_view', { page: 'Puntos de servicio be parking' });
    cargarHistorial();
  }, [cargarHistorial]);

  const sincronizarAhora = async () => {
    if (!isAdmin) {
      toast.error('Inicia sesión CMS en /login para sincronizar y editar');
      return;
    }
    setSyncing(true);
    try {
      const actuales = await cargarDesdeFirestore();
      if (actuales && actuales.length > 0) {
        toast.info('Ya hay puntos en Firestore. Usa editar / eliminar / +');
        setHistorial(actuales);
        return;
      }
      const apiItems = await cargarDesdeApi();
      const sembrados = await intentarSembrarFirestore(apiItems);
      if (sembrados && sembrados.length > 0) {
        toast.success(`Se guardaron ${sembrados.length} puntos en Firestore`);
        setHistorial(sembrados);
      }
    } catch (e) {
      toast.error('No se pudo sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const validateFields = ({ nombre, direccion, ciudad }) => {
    const n = String(nombre || '').trim();
    const d = String(direccion || '').trim();
    const c = String(ciudad || '').trim();
    if (!n || !d || !c) {
      toast.error('Todos los campos son obligatorios');
      return null;
    }
    if (n.length > MAX_LEN || d.length > MAX_LEN || c.length > MAX_LEN) {
      toast.error(`Cada campo admite máximo ${MAX_LEN} caracteres`);
      return null;
    }
    return { nombre: n, direccion: d, ciudad: c };
  };

  const ensureFirestoreEditable = (item) => {
    if (item.fromApi) {
      toast.error(
        'Los datos aún vienen de la API. Pulsa "Sincronizar a Firestore" o inicia sesión CMS.'
      );
      return false;
    }
    return true;
  };

  const startEdit = (item) => {
    if (!ensureFirestoreEditable(item)) return;
    setEditingId(item.id);
    setEditForm({
      nombre: item.nombre,
      direccion: item.direccion,
      ciudad: item.ciudad,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(emptyForm);
  };

  const saveEdit = async (item) => {
    const data = validateFields(editForm);
    if (!data) return;
    setSaving(true);
    try {
      await db.collection(COLLECTION).doc(item.id).update({
        ...data,
        cmsTipo: CMS_TIPO,
        updatedAt: new Date(),
      });
      setHistorial((prev) =>
        prev.map((row) => (row.id === item.id ? { ...row, ...data } : row))
      );
      toast.success('Parqueadero guardado correctamente');
      cancelEdit();
    } catch (e) {
      console.error(e);
      toast.error('No se pudo guardar. ¿Sesión CMS activa?');
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    if (!isAdmin) {
      toast.error('Inicia sesión CMS en /login para añadir');
      return;
    }
    setAddForm(emptyForm);
    setShowAdd(true);
  };

  const submitAdd = async () => {
    const data = validateFields(addForm);
    if (!data) return;
    setAdding(true);
    try {
      // Si aún estamos en modo API, sembrar antes de añadir
      if (historial.some((r) => r.fromApi)) {
        const sembrados = await intentarSembrarFirestore(
          historial.map((r, i) => ({ ...r, orden: i + 1 }))
        );
        if (!sembrados) {
          setAdding(false);
          return;
        }
        setHistorial(sembrados);
      }

      const nextOrden =
        (historial.reduce((max, r) => Math.max(max, r.orden || 0), 0) || 0) + 1;
      const ref = await db.collection(COLLECTION).add({
        ...data,
        cmsTipo: CMS_TIPO,
        orden: nextOrden,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      setHistorial((prev) => {
        const base = prev.filter((r) => !r.fromApi);
        return [...base, { id: ref.id, ...data, orden: nextOrden }];
      });
      setShowAdd(false);
      setAddForm(emptyForm);
      toast.success('Parqueadero registrado correctamente');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo registrar el parqueadero');
    } finally {
      setAdding(false);
    }
  };

  const openDelete = (item) => {
    if (!ensureFirestoreEditable(item)) return;
    setDeleteItem(item);
    setConfirmText('');
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (confirmText.trim().toLowerCase() !== 'confirmar') {
      toast.error('Debes escribir la palabra "confirmar" para eliminar');
      return;
    }
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await db.collection(COLLECTION).doc(deleteItem.id).delete();
      setHistorial((prev) => prev.filter((r) => r.id !== deleteItem.id));
      setShowDelete(false);
      setDeleteItem(null);
      setConfirmText('');
      toast.success('Parqueadero eliminado');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo eliminar el parqueadero');
    } finally {
      setDeleting(false);
    }
  };

  const onChangeLimited = (setter, field) => (e) => {
    const value = e.target.value.slice(0, MAX_LEN);
    setter((prev) => ({ ...prev, [field]: value }));
  };

  const fromApi = historial.some((r) => r.fromApi);

  if (loading) {
    return (
      <div className="cargando">
        <div className="loader" id="loader">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <>
      <Container className="pagina-puntos-servicio">
        <Row
          className="justify-content-center mtop-50 mbottom-20"
          id="contacto"
        >
          <Col xs={12}>
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h3 className="texto-titulo mb-0">
                Puntos de servicio be parking
              </h3>
              <div className="d-flex align-items-center" style={{ gap: 10 }}>
                {isAdmin && fromApi && (
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={syncing}
                    onClick={sincronizarAhora}
                  >
                    {syncing ? 'Sincronizando…' : 'Sincronizar a Firestore'}
                  </Button>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    className="btn-add-punto"
                    onClick={openAdd}
                    title="Añadir parqueadero"
                  >
                    <FaPlus />
                  </button>
                )}
              </div>
            </div>
            {isAdmin && fromApi && (
              <p className="text-muted mtop-10 mb-0">
                Estás viendo la API. Pulsa &quot;Sincronizar a Firestore&quot;
                para poder editar.
              </p>
            )}
          </Col>
        </Row>

        <Row className="justify-content-center mbottom-50">
          <Col xs={12}>
            <Table bordered striped className="tabla">
              <thead>
                <tr>
                  <th>Puntos de servicio be parking</th>
                  <th>Dirección</th>
                  <th>Ciudad</th>
                  {isAdmin && <th style={{ width: 120 }}>Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {historial.map((item) => {
                  const editing = editingId === item.id;
                  return (
                    <tr key={item.id}>
                      <td>
                        {editing ? (
                          <Form.Control
                            value={editForm.nombre}
                            maxLength={MAX_LEN}
                            onChange={onChangeLimited(setEditForm, 'nombre')}
                          />
                        ) : (
                          item.nombre
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <Form.Control
                            value={editForm.direccion}
                            maxLength={MAX_LEN}
                            onChange={onChangeLimited(setEditForm, 'direccion')}
                          />
                        ) : (
                          item.direccion
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <Form.Control
                            value={editForm.ciudad}
                            maxLength={MAX_LEN}
                            onChange={onChangeLimited(setEditForm, 'ciudad')}
                          />
                        ) : (
                          item.ciudad
                        )}
                      </td>
                      {isAdmin && (
                        <td>
                          <div className="puntos-acciones">
                            {editing ? (
                              <>
                                <button
                                  type="button"
                                  className="edit-button button-tooltip"
                                  data-tooltip="Guardar"
                                  disabled={saving}
                                  onClick={() => saveEdit(item)}
                                >
                                  <FaSave className="icon" />
                                </button>
                                <button
                                  type="button"
                                  className="close-button button-tooltip"
                                  data-tooltip="Cancelar"
                                  disabled={saving}
                                  onClick={cancelEdit}
                                >
                                  <FaTimes className="icon" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="edit-button button-tooltip"
                                  data-tooltip="Editar"
                                  onClick={() => startEdit(item)}
                                >
                                  <FaEdit className="icon" />
                                </button>
                                <button
                                  type="button"
                                  className="delete-button button-tooltip"
                                  data-tooltip="Eliminar"
                                  onClick={() => openDelete(item)}
                                >
                                  <FaTrash className="icon" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            {isAdmin && (
              <div className="text-center mtop-20">
                <button
                  type="button"
                  className="btn-add-punto"
                  onClick={openAdd}
                  title="Añadir parqueadero"
                >
                  <FaPlus />
                </button>
              </div>
            )}
          </Col>
        </Row>
      </Container>

      <Modal show={showAdd} onHide={() => !adding && setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Añadir parqueadero</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              value={addForm.nombre}
              maxLength={MAX_LEN}
              onChange={onChangeLimited(setAddForm, 'nombre')}
              placeholder="Máx. 70 caracteres"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Dirección *</Form.Label>
            <Form.Control
              value={addForm.direccion}
              maxLength={MAX_LEN}
              onChange={onChangeLimited(setAddForm, 'direccion')}
              placeholder="Máx. 70 caracteres"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Ciudad *</Form.Label>
            <Form.Control
              value={addForm.ciudad}
              maxLength={MAX_LEN}
              onChange={onChangeLimited(setAddForm, 'ciudad')}
              placeholder="Máx. 70 caracteres"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={adding}
            onClick={() => setShowAdd(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" disabled={adding} onClick={submitAdd}>
            {adding ? 'Guardando…' : 'Añadir'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDelete}
        onHide={() => !deleting && setShowDelete(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Eliminar parqueadero</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Para eliminar este parqueadero escriba la palabra{' '}
            <strong>&quot;confirmar&quot;</strong>
          </p>
          {deleteItem && (
            <p className="text-muted mb-3">
              {deleteItem.nombre} — {deleteItem.direccion} — {deleteItem.ciudad}
            </p>
          )}
          <Form.Control
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder='Escribe "confirmar"'
            autoComplete="off"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={deleting}
            onClick={() => setShowDelete(false)}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={
              deleting || confirmText.trim().toLowerCase() !== 'confirmar'
            }
            onClick={confirmDelete}
          >
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <FooterBeparking />
    </>
  );
};

export default PuntosbePage;
